import { type AxiosRequestConfig, isAxiosError } from "axios"

import { MULTIPART, adoptForm } from "../../../components/Esign/signing/adopt-form.ts"
import { bytesOf, jsonBody } from "../../../components/Esign/signing/bytes.ts"
import { fieldActions } from "../../../components/Esign/signing/field-actions.ts"
import { shareInFlight } from "../../../components/Esign/signing/in-flight.ts"
import {
  accepted,
  markOutcome,
  toOutcome
} from "../../../components/Esign/signing/outcome.ts"
import type {
  MarkKind,
  Outcome,
  SigningState,
  SigningTransport
} from "../../../components/Esign/signing/types.ts"
import { publicApi } from "../../../services/public-api"
import { withSession } from "./signing.client"
import {
  type SigningResponse,
  downloadCertificate,
  downloadDocument,
  fetchDocument
} from "./signing.services"
import type { SigningRefusal } from "../types/signing.types"

const ROOT = "/esign/public"
const NOTICE = "notice_of_cancellation"
const SIGNING_UNAVAILABLE = 503

interface Answer {
  status: number
  data: unknown
}

// publicApi throws on 5xx; a 503 is signing switched off with a reason, so it comes back as an answer.
const unavailableAnswer = (error: unknown): Answer | null =>
  isAxiosError(error) && error.response?.status === SIGNING_UNAVAILABLE
    ? { status: error.response.status, data: error.response.data }
    : null

const answered = async (request: () => Promise<Answer>): Promise<Answer> => {
  try {
    return await request()
  } catch (error) {
    const answer = unavailableAnswer(error)
    if (!answer) throw error
    return answer
  }
}

const refused = <T>(status: number, refusal: SigningRefusal | null): Outcome<T> => ({
  ok: false,
  status,
  data: null,
  reason: refusal?.reason ?? null,
  detail: refusal?.detail ?? null
})

// `preloaded` serves the first read: every read of the document is an audited opening.
export const createPublicTransport = (
  session: string,
  preloaded?: Blob | null
): SigningTransport => {
  const auth = withSession(session)
  const multipart = { headers: { ...auth.headers, ...MULTIPART } }
  let unused = preloaded ?? null
  const post = async <T>(
    path: string,
    body: unknown,
    config: AxiosRequestConfig = auth
  ) => {
    const { status, data } = await answered(() =>
      publicApi.post(`${ROOT}/${path}`, body, config)
    )
    return toOutcome<T>(status, data)
  }

  const readDocument = async (): Promise<Outcome<Uint8Array>> => {
    if (unused) {
      const held = unused
      unused = null
      return accepted(200, await bytesOf(held))
    }
    let response: Awaited<ReturnType<typeof fetchDocument>>
    try {
      response = await fetchDocument(session)
    } catch (error) {
      const answer = unavailableAnswer(error)
      if (!answer) throw error
      return toOutcome<Uint8Array>(answer.status, await jsonBody(answer.data))
    }
    if (!response.ok || !response.blob) return refused(response.status, response.refusal)
    // Only the bytes are wanted here; the blob URL fetchDocument made would leak.
    if (response.data) URL.revokeObjectURL(response.data)
    return accepted(response.status, await bytesOf(response.blob))
  }

  const readNotice = async (): Promise<Outcome<Uint8Array>> => {
    let response: Awaited<ReturnType<typeof fetchDocument>>
    try {
      response = await fetchDocument(session, { document: NOTICE })
    } catch (error) {
      const answer = unavailableAnswer(error)
      if (!answer) throw error
      return toOutcome<Uint8Array>(answer.status, await jsonBody(answer.data))
    }
    if (!response.ok || !response.blob) return refused(response.status, response.refusal)
    if (response.data) URL.revokeObjectURL(response.data)
    return accepted(response.status, await bytesOf(response.blob))
  }

  const saved = async (
    save: () => Promise<SigningResponse<unknown>>
  ): Promise<Outcome<unknown>> => {
    try {
      const response = await save()
      return response.ok
        ? accepted(response.status, null)
        : refused(response.status, response.refusal)
    } catch (error) {
      const answer = unavailableAnswer(error)
      if (!answer) throw error
      return toOutcome(answer.status, await jsonBody(answer.data))
    }
  }

  return {
    getState: async () => {
      const { status, data } = await answered(() => publicApi.get(`${ROOT}/fields/`, auth))
      return toOutcome<SigningState>(status, data)
    },
    getDocument: shareInFlight(readDocument),
    getNotice: shareInFlight(readNotice),
    download: (filename) => saved(() => downloadDocument(session, filename)),
    downloadNotice: (filename) =>
      saved(() => downloadDocument(session, filename, NOTICE)),
    downloadCertificate: (filename) =>
      saved(() => downloadCertificate(session, filename)),
    adopt: (input) => post("adopt/", adoptForm(input), multipart),
    getAdoptedMark: async (kind: MarkKind) => {
      const { status, data } = await answered(() =>
        publicApi.get(`${ROOT}/adopt/${kind}/`, { ...auth, responseType: "blob" })
      )
      return markOutcome(status, data)
    },
    ...fieldActions(post),
    pageViewed: async (page) => {
      try {
        await publicApi.post(`${ROOT}/page-viewed/`, { page }, auth)
      } catch {
        // A reported view is evidence the page offers, never something the signer waits on.
      }
    }
  }
}
